import React, { createContext, useContext, useEffect, ReactNode } from "react";
import { map, pick } from "lodash";
import useLocalStorage from "../hooks/useLocalStorage";
import {
  InventoryData,
  InventoryRecord,
  ProductLocationsData,
} from "../interfaces/product";
import { useFetchContext } from "./FetchContext";
import { IDataPagination } from "../interfaces";
import { useAuthContext } from "./AuthContext";

const initialInventoryData: InventoryData = {
  totalInStock: 0,
  totalInventory: 0,
  inventoryData: [],
};

const InventoryContext = createContext<{
  inventoryData: InventoryData;
  setProductStocked: (
    data: Pick<InventoryRecord, "serialNumber" | "productCode">
  ) => void;
  resetInventoryData: () => void;
}>({
  inventoryData: initialInventoryData,
  setProductStocked: (
    data: Pick<InventoryRecord, "serialNumber" | "productCode">
  ) => {},
  resetInventoryData: () => {},
});

interface InventoryProviderProps {
  children: ReactNode;
}

export const useInventoryContext = () => {
  const context = useContext(InventoryContext);
  return context;
};

export const InventoryProvider: React.FC<InventoryProviderProps> = ({
  children,
}) => {
  const { fetchData } = useFetchContext();
  const { userData } = useAuthContext();
  const [inventoryData, setInventoryData, resetData] =
    useLocalStorage<InventoryData>(
      `inventory-data-${userData?.user?.warehouse?.id}`,
      initialInventoryData
    );

  const initInventoryData = async () => {
    console.log("initInventoryData");
    try {
      const response = await fetchData<IDataPagination<ProductLocationsData>>(
        `/products/locations?page=1&pageSize=9999`
      );
      setInventoryData({
        totalInStock:
          response.data.items?.filter((item) => item.quantity)?.length ?? 0,
        totalInventory: 0,
        inventoryData: map(
          response.data.items?.filter((item) => item.quantity),
          (item): InventoryRecord => ({
            ...pick(item, ["serialNumber", "productCode"]),
            isStocked: false,
          })
        ),
      });
    } catch (error) {}
  };

  const resetInventoryData = async () => {
    resetData();
    window.location.reload();
  };

  useEffect(() => {
    setInventoryData((prevInventoryData) => {
      return {
        ...prevInventoryData,
        totalInventory: prevInventoryData?.inventoryData?.filter(
          (item) => item.isStocked
        )?.length,
      };
    });
    // eslint-disable-next-line
  }, [inventoryData?.inventoryData]);

  const setProductStocked = ({
    serialNumber,
    productCode,
  }: Partial<InventoryRecord>) => {
    if (serialNumber) {
      setInventoryData((prevInventoryData) => {
        return {
          ...prevInventoryData,
          inventoryData: prevInventoryData?.inventoryData?.map((item) => {
            if (item.serialNumber === serialNumber) {
              return {
                ...item,
                isStocked: true,
              };
            }
            return item;
          }),
        };
      });
    } else if (productCode) {
      setInventoryData((prevInventoryData) => {
        return {
          ...prevInventoryData,
          inventoryData: prevInventoryData?.inventoryData?.map((item) => {
            if (item.productCode === productCode) {
              return {
                ...item,
                isStocked: true,
              };
            }
            return item;
          }),
        };
      });
    }
  };

  useEffect(() => {
    if (!inventoryData?.inventoryData?.length) {
      initInventoryData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <InventoryContext.Provider
      value={{
        inventoryData: inventoryData || initialInventoryData,
        setProductStocked,
        resetInventoryData,
      }}
    >
      {children}
    </InventoryContext.Provider>
  );
};
